// Keeps the screen awake while a long-running AI request is in flight. A
// multi-page lab report can take 30-110s (see analyze-lab-report) — without
// this, the phone's own screen-lock timer fires mid-request, and on a
// locked/dimmed screen the browser can suspend or discard the tab, losing
// all in-memory state (the open form, the upload). Best-effort: unsupported
// browsers, or a lock request rejected because the tab is already hidden,
// just let the request run unprotected instead of failing it.
export async function withWakeLock<T>(fn: () => Promise<T>): Promise<T> {
  // A plain `let` here defeats TS's closure narrowing (the compiler can't
  // tell `acquire` and `onVisibilityChange` reassign the same binding) —
  // a boxed ref sidesteps that.
  const held: { sentinel: WakeLockSentinel | null } = { sentinel: null };

  const acquire = async () => {
    try {
      held.sentinel = 'wakeLock' in navigator ? await navigator.wakeLock.request('screen') : null;
    } catch {
      held.sentinel = null;
    }
  };

  // The lock is auto-released whenever the document goes hidden (app
  // switch, etc.) — re-acquire it once the page is visible again, in case
  // the request is still running.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && !held.sentinel) void acquire();
  };

  await acquire();
  document.addEventListener('visibilitychange', onVisibilityChange);
  try {
    return await fn();
  } finally {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    void held.sentinel?.release().catch(() => {});
  }
}
